import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'semantic';
import Lookup from './Steps/Lookup';
import Plans from './Steps/Plans';
import AccountBilling from './Steps/account-billing';
import Location from './Steps/Location';
import Details from './Steps/Details';
import DetailsInvite from './Steps/DetailsInvite';
import { WizardProvider } from './context';
import { useHistory } from 'react-router-dom';
import { useAccount } from 'contexts/account';
import { useFeatures } from 'contexts/features';
import { EvseInvite } from 'types/evse-invite';
import { ApiError, request } from 'utils/api';
import { User } from 'types/user';
import { EvseLinkRequest } from 'types/evse-link';
import { Account } from 'types/account';
import { FormikProps } from 'formik';
import { AccountBillingValues } from './Steps/account-billing/formData';
import {
  PaymentMethodsWidgetCallbacks,
  tryStoringPaymentInstrument,
} from 'components/PaymentMethodsWidget/PaymentMethodsWidget';
import { useEnabledPaymentMethods } from 'contexts/enabledPaymentMethods';
import { useToast } from 'components/Toast/useToast';

type Props = {
  onClose?: () => void;
  onSuccess?: () => void;
  trigger?: ReactElement;
  evseInvite?: EvseInvite;
  newStationSerialFromURL?: string;
  isOpen: boolean;
};

async function submitUserCreditBilling(creditBilling: User['creditBilling']) {
  await request({
    method: 'PATCH',
    path: '/1/users/me',
    body: {
      creditBilling,
    },
  });
}

async function submitAccountCreditBilling(
  creditBilling: Account['creditBilling']
) {
  await request({
    method: 'PATCH',
    path: '/1/accounts/self',
    body: {
      creditBilling,
    },
  });
}

async function submitEvseLinkRequest(body: EvseLinkRequest) {
  const { data } = await request({
    method: 'POST',
    path: '/1/evse-controllers/link',
    body,
  });

  return data;
}

async function submitStickerRequest(stationId: string) {
  await request({
    method: 'POST',
    path: `/1/evse-controllers/${stationId}/evse-id-assignment-request`,
    body: {
      source: 'registration',
    },
  });
}

async function handleLinkEvseInviteSubmit(
  userCreditBilling: User['creditBilling'],
  evseLinkRequest: EvseLinkRequest,
  wantSticker: boolean,
  stationId: string
) {
  await submitUserCreditBilling(userCreditBilling);
  await submitEvseLinkRequest(evseLinkRequest);
  if (wantSticker) {
    await submitStickerRequest(stationId);
  }
}

async function handleLinkEvseSubmit(
  useAccountCreditBilling: boolean,
  creditBilling: User['creditBilling'] | Account['creditBilling'],
  evseLinkRequest: EvseLinkRequest,
  wantSticker: boolean,
  stationId: string
) {
  try {
    if (useAccountCreditBilling) {
      await submitAccountCreditBilling(creditBilling);
    } else {
      await submitUserCreditBilling(creditBilling);
    }
  } catch (e) {
    // e is always ApiError (coming from request)
    throw new CreditBillingRequestError(e as ApiError);
  }

  try {
    await submitEvseLinkRequest(evseLinkRequest);
  } catch (e) {
    throw new EvseLinkRequestError(e as ApiError);
  }

  if (wantSticker) {
    try {
      await submitStickerRequest(stationId);
    } catch (e) {
      throw new StickerRequestError(e as ApiError);
    }
  }
}

type EvseLinkRequests = {
  evseLinkRequest: EvseLinkRequest;
  wantSticker: boolean;
  useAccountCreditBilling: boolean;
  creditBilling: User['creditBilling'] | Account['creditBilling'];
  stationId: string;
};

export default ({
  onClose = undefined,
  onSuccess = undefined,
  trigger = undefined,
  evseInvite = undefined,
  newStationSerialFromURL = undefined,
  isOpen = false,
}: Props) => {
  const [open, setOpen] = useState(isOpen);
  const [step, setStep] = useState('lookup');
  const [creditBillingError, setCreditBillingError] = useState<string>();
  const [linkEvseError, setLinkEvseError] = useState<string>();
  const [stickerRequestError, setStickerRequestError] = useState<string>();
  const [genericError, setGenericError] = useState<string>();
  const history = useHistory();
  const { isBillingUpdateRequired } = useAccount();

  const showBillingStepRef = useRef<boolean | undefined>(false);

  const [linkRequests, setLinkRequests] = useState<EvseLinkRequests>();

  const { t } = useTranslation();
  const { hasFeature, isReady } = useFeatures();

  const { enabledPaymentMethods } = useEnabledPaymentMethods();
  const { account, getAccount } = useAccount();
  const toast = useToast();

  // Order of steps is important
  const steps = ['lookup', 'plans', 'location', 'details'];

  const showAccountBillingForm =
    isReady &&
    hasFeature('account_billing_new_charge_station') &&
    (isBillingUpdateRequired || showBillingStepRef.current);

  if (showAccountBillingForm) {
    showBillingStepRef.current = true;
    steps.push('billing');
  }

  useEffect(() => {
    if (newStationSerialFromURL) {
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    if (isOpen !== open) {
      setOpen(isOpen);
    }
  }, [isOpen]);

  function onBack() {
    const currentStep = steps.indexOf(step);
    setStep(steps[currentStep - 1]);
  }

  const setNextStep = () => {
    if (!steps.includes(step) || steps.indexOf(step) === -1) {
      setStep('lookup');
      return;
    }

    const currentStepIndex = steps.indexOf(step);
    const nextStep = steps[currentStepIndex + 1];
    setStep(nextStep);
  };

  const stepHeader =
    t('newChargingStation.header', 'New Charging Station - Step') +
    ' ' +
    (steps.indexOf(step) + 1) +
    '/' +
    steps.length +
    ': ';

  function renderDetails() {
    if (evseInvite) {
      return (
        <DetailsInvite
          stepHeader={stepHeader}
          evseInvite={evseInvite}
          onBack={onBack}
          formSubmitError={
            creditBillingError ||
            linkEvseError ||
            stickerRequestError ||
            genericError
          }
          isLast={!showAccountBillingForm}
          onSubmit={async (
            creditBilling,
            evseLinkRequest,
            wantSticker,
            stationId
          ) => {
            if (showAccountBillingForm) {
              setLinkRequests({
                evseLinkRequest,
                wantSticker,
                useAccountCreditBilling: false,
                creditBilling,
                stationId,
              });
              setNextStep();
            } else {
              handleLinkEvseSubmit(
                false,
                creditBilling,
                evseLinkRequest,
                wantSticker,
                stationId
              )
                .then(() => {
                  onSuccess?.();
                  if (evseLinkRequest.body?.locationId) {
                    history.push(
                      `/locations/${evseLinkRequest.body?.locationId}`
                    );
                  }
                  toast.success(
                    t(
                      'newChargingStationModal.evseCreated',
                      'Charge Station Successfully onboarded'
                    )
                  );
                  onClose?.();
                })
                .catch((e) => {
                  console.error('XXXX', e);
                  if (e instanceof CreditBillingRequestError) {
                    console.log('eeee', e.message);
                    setCreditBillingError(
                      t(
                        'newChargingStationModal.creditBillingErrorWithCause',
                        'Error saving credit billing, {{ERROR_MESSAGE}}',
                        { ERROR_MESSAGE: e.message }
                      )
                    );
                  } else if (e instanceof EvseLinkRequestError) {
                    setLinkEvseError(
                      t(
                        'newChargingStationModal.evseLinkRequestError',
                        'Error requesting EVSE link, {{ERROR_MESSAGE}}',
                        { ERROR_MESSAGE: e.message || '' }
                      )
                    );
                  } else if (e instanceof StickerRequestError) {
                    setStickerRequestError(
                      t(
                        'newChargingStationModal.stickerRequestError',
                        'Error requesting sticker, {{ERROR_MESSAGE}}',
                        { ERROR_MESSAGE: e.message || '' }
                      )
                    );
                  } else {
                    setGenericError(
                      t(
                        'newChargingStationModal.genericError',
                        'An unexpected error occurred, please retry the request, {{ERROR_MESSAGE}}',
                        { ERROR_MESSAGE: e.message || '' }
                      )
                    );
                  }
                });
            }
          }}
        />
      );
    }

    return (
      <Details
        stepHeader={stepHeader}
        onBack={onBack}
        isLast={!showAccountBillingForm}
        formSubmitError={
          creditBillingError ||
          linkEvseError ||
          stickerRequestError ||
          genericError
        }
        onSubmit={async (
          reimbursementType,
          creditBilling,
          evseLinkRequest,
          wantSticker,
          stationId
        ) => {
          const useAccountCreditBilling =
            reimbursementType === 'employeeReimbursement' ||
            reimbursementType === 'privateChargingWithReimbursement';

          if (showAccountBillingForm) {
            setNextStep();

            setLinkRequests({
              evseLinkRequest,
              wantSticker,
              useAccountCreditBilling,
              creditBilling,
              stationId,
            });
          } else {
            handleLinkEvseSubmit(
              useAccountCreditBilling,
              creditBilling,
              evseLinkRequest,
              wantSticker,
              stationId
            )
              .then(() => {
                onSuccess?.();
                if (evseLinkRequest?.body?.locationId) {
                  history.push(`/locations/${evseLinkRequest.body.locationId}`);
                }
                toast.success(
                  t(
                    'newChargingStationModal.evseCreated',
                    'Charge Station Successfully onboarded'
                  )
                );
                onClose?.();
              })
              .catch((e) => {
                console.error(e);
                if (e instanceof CreditBillingRequestError) {
                  setCreditBillingError(
                    t(
                      'newChargingStationModal.creditBillingErrorWithCause',
                      'Error saving credit billing, {{ERROR_MESSAGE}}',
                      { ERROR_MESSAGE: e.message }
                    )
                  );
                } else if (e instanceof EvseLinkRequestError) {
                  setLinkEvseError(
                    t(
                      'newChargingStationModal.evseLinkRequestError',
                      'Error requesting EVSE link, {{ERROR_MESSAGE}}',
                      { ERROR_MESSAGE: e.message || '' }
                    )
                  );
                } else if (e instanceof StickerRequestError) {
                  setStickerRequestError(
                    t(
                      'newChargingStationModal.stickerRequestError',
                      'Error requesting sticker, {{ERROR_MESSAGE}}',
                      { ERROR_MESSAGE: e.message || '' }
                    )
                  );
                } else {
                  setGenericError(
                    t(
                      'newChargingStationModal.genericError',
                      'An unexpected error occurred, please retry the request {{ERROR_MESSAGE}}',
                      { ERROR_MESSAGE: e.message || '' }
                    )
                  );
                }
              });
          }
        }}
      />
    );
  }

  useEffect(() => {
    if (!enabledPaymentMethods || !account) {
      return;
    }

    const pendingLinkRequests = getPendingLinkRequests();

    tryStoringPaymentInstrument(account, enabledPaymentMethods)
      .then((PISaved) => {
        // If not this is a fresh visit, no need to do anything
        if (PISaved) {
          toast.info(
            t(
              'paymentMethodsWidget.paymentInstrumentSaved',
              'Payment instrument saved'
            )
          );
          getAccount?.();
          if (pendingLinkRequests) {
            handleLinkEvseSubmit(
              pendingLinkRequests.useAccountCreditBilling,
              pendingLinkRequests.creditBilling,
              pendingLinkRequests.evseLinkRequest,
              pendingLinkRequests.wantSticker,
              pendingLinkRequests.stationId
            )
              .then(() => {
                clearPendingLinkRequests();
                toast.success(
                  t(
                    'newChargingStationModal.evseCreated',
                    'Charge Station Successfully onboarded'
                  )
                );
                onClose?.();
              })
              .catch((e) => {
                clearPendingLinkRequests();
                console.error(e);
                toast.error(
                  t(
                    'newChargingStationModal.evseRequestError',
                    'Error onboarding charging station, please retry the request'
                  )
                );
              });
          }
        }
      })
      .catch((e) => {
        toast.error(
          t(
            'paymentMethodsWidget.paymentInstrumentError',
            'Error saving payment instrument'
          )
        );
      });
  }, [enabledPaymentMethods, account]);

  const billingFormRef = useRef<FormikProps<AccountBillingValues> | null>(null);
  const paymentMethodWidgetRef = useRef<PaymentMethodsWidgetCallbacks | null>(
    null
  );

  return (
    <Modal
      closeIcon
      closeOnDimmerClick={false}
      trigger={trigger}
      onClose={() => {
        setOpen(false);
        if (onClose) onClose();
      }}
      onOpen={() => setOpen(true)}
      open={open}>
      <WizardProvider>
        {step === 'lookup' && (
          <Lookup
            stepHeader={stepHeader}
            onDone={() => setNextStep()}
            defaultSerialNumber={newStationSerialFromURL}
          />
        )}
        {step === 'plans' && (
          <Plans
            stepHeader={stepHeader}
            onBack={onBack}
            onDone={() => setNextStep()}
          />
        )}
        {step === 'billing' && (
          <AccountBilling
            stepHeader={stepHeader}
            onBack={onBack}
            billingFormRef={billingFormRef}
            paymentMethodWidgetRef={paymentMethodWidgetRef}
            onBillingDetailsSaved={() => {
              // This means the user has already clicked Next, so we need to save pending link requests
              // and move to iDeal for authorization. Once back we can send the link request.
              if (!linkRequests) {
                return;
              }
              savePendingLinkRequests(linkRequests);
              paymentMethodWidgetRef.current
                ?.authorizeActiveOption()
                .then((authResponse) => {
                  if (authResponse.success) {
                    if (authResponse.redirectUrl) {
                      window.location.href = authResponse.redirectUrl;
                    } else {
                      if (evseInvite) {
                        handleLinkEvseInviteSubmit(
                          linkRequests.creditBilling,
                          linkRequests.evseLinkRequest,
                          linkRequests.wantSticker,
                          linkRequests.stationId
                        );
                      } else {
                        handleLinkEvseSubmit(
                          linkRequests.useAccountCreditBilling,
                          linkRequests.creditBilling,
                          linkRequests.evseLinkRequest,
                          linkRequests.wantSticker,
                          linkRequests.stationId
                        );
                      }
                    }
                  }
                })
                .catch((e) => {
                  console.error(e);
                });
            }}
            onDone={() => {
              // Billing is already saved, so we just need to post the requests
              if (!linkRequests) {
                return;
              }
              if (evseInvite) {
                handleLinkEvseInviteSubmit(
                  linkRequests.creditBilling,
                  linkRequests.evseLinkRequest,
                  linkRequests.wantSticker,
                  linkRequests.stationId
                );
              } else {
                handleLinkEvseSubmit(
                  linkRequests.useAccountCreditBilling,
                  linkRequests.creditBilling,
                  linkRequests.evseLinkRequest,
                  linkRequests.wantSticker,
                  linkRequests.stationId
                );
              }

              setOpen(false);
            }}
          />
        )}
        {step === 'location' && (
          <Location
            stepHeader={stepHeader}
            evseInvite={evseInvite}
            onBack={onBack}
            onDone={() => setNextStep()}
          />
        )}

        {step === 'details' && renderDetails()}
      </WizardProvider>
    </Modal>
  );
};

const savePendingLinkRequests = (reqs: EvseLinkRequests) => {
  localStorage.setItem('pendingLinkRequests', JSON.stringify(reqs));
};

const getPendingLinkRequests = (): EvseLinkRequests | undefined => {
  const pendingLinkRequests = localStorage.getItem('pendingLinkRequests');
  if (pendingLinkRequests) {
    return JSON.parse(pendingLinkRequests);
  }
  return undefined;
};

const clearPendingLinkRequests = () => {
  localStorage.removeItem('pendingLinkRequests');
};

class CreditBillingRequestError extends Error {
  constructor(error: ApiError) {
    super(error.message);
  }
}

class EvseLinkRequestError extends Error {
  constructor(error: ApiError) {
    super(error.message);
  }
}

class StickerRequestError extends Error {
  constructor(error: ApiError) {
    super(error.message);
  }
}
